import { z } from 'zod';
import { DomainError } from '../errors.js';
import { toIssues } from '../materials/catalog.js';
import type { ParseResult } from '../voyage/schemas.js';
import { DEFAULT_PERFORMANCE_CRITERIA, type DrillCriterion } from './template.js';

/**
 * Tatbikat icra kaydı (Execution Wizard; ortağın belgesi §5; ADR 0128 Faz 3 Ek). Altı aşama:
 * A General Information, B Preparation, C Participants, D Execution, E Evaluation, F Close. Kayıt
 * gemide yazılır, ara kayıtla aşama aşama dolar, Responsible Officer "Submit for Master review"
 * ile gönderir; onay ya da iade kaptanındır (ADR 0127). Saf modül: şemalar gemi, bulut ve
 * arayüzde ortak; durum makinesi olay izinden türer.
 */

/* ---------- Durum makinesi ---------- */

/**
 * - `in_progress`: düzenlenebilir (yeni, iade edilmiş ya da yeniden açılmış kayıt).
 * - `submitted`: kaptan incelemesinde; düzenlenmez.
 * - `approved`: kaptan onaylı; değiştirilemez, düzeltme yeniden açmayla (gerekçeli, denetim izi).
 * - `stopped`: gerçek acil durumda durduruldu ("Stopped: real emergency"); olay raporuna bağlanır.
 * - `cancelled`: yanlışlıkla başlatılmış kayıt gerekçeyle iptal (silme yok).
 */
export const DRILL_RECORD_STATES = [
  'in_progress',
  'submitted',
  'approved',
  'stopped',
  'cancelled',
] as const;
export type DrillRecordState = (typeof DRILL_RECORD_STATES)[number];

export const DRILL_RECORD_ACTIONS = [
  'save',
  'submit',
  'approve',
  'return',
  'reopen',
  'stop',
  'link_incident',
  'cancel',
] as const;
export type DrillRecordAction = (typeof DRILL_RECORD_ACTIONS)[number];

const TRANSITIONS: Readonly<
  Record<DrillRecordState, Partial<Record<DrillRecordAction, DrillRecordState>>>
> = {
  in_progress: { save: 'in_progress', submit: 'submitted', stop: 'stopped', cancel: 'cancelled' },
  submitted: { approve: 'approved', return: 'in_progress' },
  approved: { reopen: 'in_progress' },
  stopped: { link_incident: 'stopped' },
  cancelled: {},
};

/** Geçiş; olanaksızsa `INVALID_TRANSITION` (ADR 0089 §3 kalıbı). */
export function drillRecordTransition(
  state: DrillRecordState,
  action: DrillRecordAction,
): DrillRecordState {
  const next = TRANSITIONS[state][action];
  if (!next) throw new DomainError('INVALID_TRANSITION', `${state}:${action}`);
  return next;
}

/** Değiştirilemez olay (`drill_record_events`): gönderim, yeniden açma, durdurma, iptal. */
export const DRILL_RECORD_EVENT_KINDS = ['submitted', 'reopened', 'stopped', 'cancelled'] as const;
export type DrillRecordEventKind = (typeof DRILL_RECORD_EVENT_KINDS)[number];

const EVENT_ACTION: Readonly<Record<DrillRecordEventKind, DrillRecordAction>> = {
  submitted: 'submit',
  reopened: 'reopen',
  stopped: 'stop',
  cancelled: 'cancel',
};

export interface DrillRecordStateView {
  state: DrillRecordState;
  /** Son gönderim kaptanca iade edildi (düzeltilip yeniden gönderilir). */
  returned: boolean;
  /** Onaylı kayıt yeniden açıldı (düzeltme). */
  reopened: boolean;
}

/**
 * Durum saklanmaz, olay izinden türer (ADR 0089 §1 kalıbı): olaylar zaman sırasıyla, gönderimin
 * kaptan kararıyla (`drill_decisions`). Olanaksız iz `INVALID_TRANSITION`.
 */
export function drillRecordState(
  events: readonly { kind: DrillRecordEventKind; decision: 'approved' | 'returned' | null }[],
): DrillRecordStateView {
  let state: DrillRecordState = 'in_progress';
  let returned = false;
  let reopened = false;
  for (const event of events) {
    state = drillRecordTransition(state, EVENT_ACTION[event.kind]);
    returned = false;
    reopened = event.kind === 'reopened';
    if (event.kind === 'submitted' && event.decision !== null) {
      state = drillRecordTransition(state, event.decision === 'approved' ? 'approve' : 'return');
      returned = event.decision === 'returned';
    }
  }
  return { state, returned, reopened };
}

/* ---------- Şema yardımcıları ---------- */

const missing = (input: unknown) => input === undefined || input === null;
const typed = (code: string) => ({
  error: (issue: { input?: unknown }) => (missing(issue.input) ? 'REQUIRED' : code),
});
const collapse = (value: string) => value.normalize('NFC').trim().replace(/\s+/g, ' ');
const paragraph = (value: string) =>
  value
    .normalize('NFC')
    .split(/\r\n|\r|\n/)
    .map((line) => line.trim().replace(/[ \t]+/g, ' '))
    .join('\n')
    .replace(/\n{3,}/g, '\n\n')
    .trim();
const optionalText = (max: number) =>
  z
    .string(typed('INVALID_TEXT'))
    .transform(collapse)
    .pipe(z.string().max(max, 'TOO_LONG'))
    .transform((value) => (value === '' ? null : value))
    .nullish()
    .transform((value) => value ?? null);
const optionalParagraph = (max: number) =>
  z
    .string(typed('INVALID_TEXT'))
    .transform(paragraph)
    .pipe(z.string().max(max, 'TOO_LONG'))
    .transform((value) => (value === '' ? null : value))
    .nullish()
    .transform((value) => value ?? null);
const requiredParagraph = (max: number) =>
  z
    .string(typed('INVALID_TEXT'))
    .transform(paragraph)
    .pipe(z.string().min(1, 'REQUIRED').max(max, 'TOO_LONG'));
const uuid = z.uuid('INVALID_ID');
const nullableUuid = uuid.nullish().transform((value) => value ?? null);
const nullableBoolean = z
  .boolean(typed('INVALID_BOOLEAN'))
  .nullish()
  .transform((value) => value ?? null);
const DAY = /^\d{4}-(0[1-9]|1[0-2])-(0[1-9]|[12]\d|3[01])$/;
const day = z
  .string(typed('INVALID_DATE'))
  .regex(DAY, 'INVALID_DATE')
  .refine((value) => {
    const date = new Date(`${value}T00:00:00Z`);
    return !Number.isNaN(date.getTime()) && date.toISOString().slice(0, 10) === value;
  }, 'INVALID_DATE');
/** Gemi yerel saati `HH:MM` (24 saat). */
export const DRILL_TIME_PATTERN = /^([01]\d|2[0-3]):[0-5]\d$/;
const time = z
  .string(typed('INVALID_TIME'))
  .transform((value) => value.trim())
  .pipe(
    z.union([z.literal(''), z.string().regex(DRILL_TIME_PATTERN, 'INVALID_TIME')], {
      error: 'INVALID_TIME',
    }),
  )
  .transform((value) => (value === '' ? null : value))
  .nullish()
  .transform((value) => value ?? null);
const seconds = z
  .number(typed('INVALID_NUMBER'))
  .int('INVALID_NUMBER')
  .min(0, 'OUT_OF_RANGE')
  .max(86_400, 'OUT_OF_RANGE')
  .nullish()
  .transform((value) => value ?? null);

function parsed<T>(result: z.ZodSafeParseResult<T>): ParseResult<T> {
  return result.success
    ? { ok: true, value: result.data }
    : {
        ok: false,
        issues: toIssues(result.error).filter(
          (issue, index, all) => all.findIndex((i) => i.path === issue.path) === index,
        ),
      };
}

/* ---------- Başlatma ---------- */

/**
 * Planner satırından (`planId`) ya da plansız "Conduct drill" ile başlatma. Kayıt şablonun o anki
 * değiştirilemez sürümünü (ve gemide onaylı senaryo varsa onun kararını) sabitler; Library sonra
 * güncellense de kayıt değişmez (belge §13.1).
 */
export const drillRecordStartSchema = z.strictObject({
  templateId: uuid,
  planId: nullableUuid,
  drillDate: day,
});
export type DrillRecordStartInput = z.output<typeof drillRecordStartSchema>;

export function parseDrillRecordStart(input: unknown): ParseResult<DrillRecordStartInput> {
  return parsed(drillRecordStartSchema.safeParse(input));
}

/* ---------- A. General Information ---------- */

export const drillGeneralSchema = z.strictObject({
  drillDate: day,
  startTime: time,
  endTime: time,
  /** Yer ya da mevki (liman, demir yeri, enlem/boylam). */
  location: optionalText(200),
  responsibleUserId: nullableUuid,
  /** Senaryo: şablondan (ya da gemi senaryosundan) gelir, bu tatbikata göre düzenlenir. */
  scenario: optionalParagraph(4000),
});
export type DrillGeneralInput = z.output<typeof drillGeneralSchema>;

export function parseDrillGeneral(input: unknown): ParseResult<DrillGeneralInput> {
  return parsed(drillGeneralSchema.safeParse(input));
}

/* ---------- B. Preparation ---------- */

export const drillPreparationSchema = z.strictObject({
  /** Operasyonel risk doğrulaması (ADR 0089; "RA-11 Emergency Drills" grubu, ADR 0106). */
  raVerificationId: nullableUuid,
  riskNotes: optionalParagraph(2000),
  /** Güvenlik brifingi verildi mi (`null`: cevapsız). */
  briefingDone: nullableBoolean,
  briefingNotes: optionalParagraph(2000),
  /** Ekipman hazır mı; hayırsa not zorunlu (gönderimde). */
  equipmentReady: nullableBoolean,
  equipmentNotes: optionalParagraph(2000),
  weather: optionalText(300),
  /** Operasyon kısıtları (seyir, yük, liman). */
  constraints: optionalParagraph(2000),
  /** Şablonun hazırlık maddeleri ve yapıldı mı. */
  checklist: z
    .array(
      z.strictObject({
        item: z
          .string(typed('INVALID_TEXT'))
          .transform(collapse)
          .pipe(z.string().min(1, 'REQUIRED').max(300, 'TOO_LONG')),
        done: z.boolean(typed('INVALID_BOOLEAN')),
      }),
      typed('INVALID_LIST'),
    )
    .max(60, 'TOO_MANY')
    .nullish()
    .transform((value) => value ?? []),
});
export type DrillPreparationInput = z.output<typeof drillPreparationSchema>;

export function parseDrillPreparation(input: unknown): ParseResult<DrillPreparationInput> {
  return parsed(drillPreparationSchema.safeParse(input));
}

/* ---------- C. Participants ---------- */

export const DRILL_ATTENDANCE = ['present', 'absent'] as const;
export type DrillAttendance = (typeof DRILL_ATTENDANCE)[number];

const participantSchema = z.strictObject({
  /** Gemideki kişi (o tarihte gemide atanmış); boş: listede olmayan katılımcı (ör. kılavuz). */
  userId: nullableUuid,
  name: z
    .string(typed('INVALID_TEXT'))
    .transform(collapse)
    .pipe(z.string().min(1, 'REQUIRED').max(120, 'TOO_LONG')),
  rank: optionalText(80),
  /** Atanan rol (muster list / görev kartı; öneri son kayıttan ya da şablondan). */
  role: optionalText(120),
  attendance: z
    .enum(DRILL_ATTENDANCE, typed('INVALID_OPTION'))
    .nullish()
    .transform((value) => value ?? null),
  absenceReason: optionalText(300),
});

export const drillParticipantsSchema = z
  .array(participantSchema, typed('INVALID_LIST'))
  .max(200, 'TOO_MANY')
  .superRefine((value, ctx) => {
    const seen = new Set<string>();
    for (const [index, p] of value.entries()) {
      if (p.userId === null) continue;
      if (seen.has(p.userId)) {
        ctx.addIssue({ code: 'custom', path: [index, 'userId'], message: 'DUPLICATE_PARTICIPANT' });
      }
      seen.add(p.userId);
    }
  });
export type DrillParticipantInput = z.output<typeof participantSchema>;

export function parseDrillParticipants(input: unknown): ParseResult<DrillParticipantInput[]> {
  return parsed(drillParticipantsSchema.safeParse(input));
}

/* ---------- D. Execution ---------- */

/** Adım sonucu (belge §5): Completed / Not Completed / N/A. */
export const DRILL_STEP_RESULTS = ['completed', 'not_completed', 'na'] as const;
export type DrillStepResult = (typeof DRILL_STEP_RESULTS)[number];

const stepSchema = z.strictObject({
  id: uuid,
  rowVersion: z.number(typed('INVALID_NUMBER')).int('INVALID_NUMBER').min(1, 'OUT_OF_RANGE'),
  result: z
    .enum(DRILL_STEP_RESULTS, typed('INVALID_OPTION'))
    .nullish()
    .transform((value) => value ?? null),
  remarks: optionalParagraph(2000),
  responsibleUserId: nullableUuid,
  /** Adımın yapıldığı yerel saat; gerçek tepki süresi başlangıçtan farktır. */
  doneTime: time,
});

export const drillStepsSchema = z.array(stepSchema, typed('INVALID_LIST')).max(80, 'TOO_MANY');
export type DrillStepInput = z.output<typeof stepSchema>;

export function parseDrillSteps(input: unknown): ParseResult<DrillStepInput[]> {
  return parsed(drillStepsSchema.safeParse(input));
}

/* ---------- E. Evaluation (Faz 4: belge §6, Crew Competency Matrix) ---------- */

export const DRILL_RATINGS = ['satisfactory', 'needs_improvement'] as const;
export type DrillRating = (typeof DRILL_RATINGS)[number];

const rating = z
  .enum(DRILL_RATINGS, typed('INVALID_OPTION'))
  .nullish()
  .transform((value) => value ?? null);

/**
 * Kişi bazında değerlendirme (belge §6 "Crew Competency Matrix": katılım, rol, değerlendirme, ilave
 * eğitim ihtiyacı; ADR 0128 Faz 4 Ek). Yalnız tatbikata katılan kişiye; aynı kişi bir kez. Eğitim
 * ihtiyacı burada değil, bulgu olarak açılır (Training Need).
 */
const crewRatingSchema = z.strictObject({
  userId: uuid,
  rating,
  note: optionalText(500),
});

export const drillEvaluationSchema = z.strictObject({
  results: z
    .array(
      z.strictObject({
        key: z.string(typed('INVALID_TEXT')).regex(/^[a-z][a-z0-9_]{0,39}$/, 'INVALID_KEY'),
        rating,
        seconds,
        note: optionalText(500),
      }),
      typed('INVALID_LIST'),
    )
    .max(40, 'TOO_MANY')
    .nullish()
    .transform((value) => value ?? []),
  debriefing: optionalParagraph(4000),
  crew: z
    .array(crewRatingSchema, typed('INVALID_LIST'))
    .max(200, 'TOO_MANY')
    .superRefine((value, ctx) => {
      const seen = new Set<string>();
      for (const [index, person] of value.entries()) {
        if (seen.has(person.userId)) {
          ctx.addIssue({
            code: 'custom',
            path: [index, 'userId'],
            message: 'DUPLICATE_PARTICIPANT',
          });
        }
        seen.add(person.userId);
      }
    })
    .nullish()
    .transform((value) => value ?? []),
});
export type DrillEvaluationInput = z.output<typeof drillEvaluationSchema>;

export function parseDrillEvaluation(input: unknown): ParseResult<DrillEvaluationInput> {
  return parsed(drillEvaluationSchema.safeParse(input));
}

/**
 * Değerlendirme kriterleri (belge §6; ADR 0128 Faz 4 Ek): belgenin yedi kriteri (Muster Time ve
 * Response Time ölçülen süre, ötekiler Satisfactory / Needs Improvement) her kayıtta; kayda
 * sabitlenmiş şablonun Performance Criteria'sı bunlarla birleşir. Belge sırası önce, şablonun ek
 * kriterleri şablon sırasıyla sonra. Aynı anahtarda şablonun adı, belgenin türü kullanılır.
 */
export function drillEvaluationCriteria(criteria: readonly DrillCriterion[]): DrillCriterion[] {
  const byKey = new Map(criteria.map((c) => [c.key, c]));
  const base = DEFAULT_PERFORMANCE_CRITERIA.map((c) => ({
    ...c,
    label: byKey.get(c.key)?.label ?? c.label,
  }));
  const extra = criteria.filter(
    (c, index) =>
      !DEFAULT_PERFORMANCE_CRITERIA.some((d) => d.key === c.key) &&
      criteria.findIndex((x) => x.key === c.key) === index,
  );
  return [...base, ...extra.map((c) => ({ ...c }))];
}

/**
 * Sayısal puan **şirket içi yöntemdir** (belge §6: "Sayısal puanlama varsa şirket içi
 * değerlendirme yöntemi olarak tanımlanır; mevzuat zorunluluğu gibi sunulmaz"): değerlendirilen
 * (Satisfactory / Needs Improvement) kriterlerde Satisfactory oranı. Süre kriteri puana girmez.
 */
export interface DrillEvaluationScore {
  method: 'company';
  satisfactory: number;
  rated: number;
  /** Yuvarlanmış yüzde; değerlendirilen kriter yoksa `null`. */
  percent: number | null;
}

export function drillEvaluationScore(
  criteria: readonly DrillCriterion[],
  results: readonly { key: string; rating: DrillRating | null }[],
): DrillEvaluationScore {
  const keys = new Set(criteria.filter((c) => c.kind === 'rating').map((c) => c.key));
  const rated = results.filter((r) => keys.has(r.key) && r.rating !== null);
  const satisfactory = rated.filter((r) => r.rating === 'satisfactory').length;
  return {
    method: 'company',
    satisfactory,
    rated: rated.length,
    percent: rated.length === 0 ? null : Math.round((satisfactory / rated.length) * 100),
  };
}

/* ---------- Durdurma, olay bağı, iptal, yeniden açma ---------- */

/** "Stopped: real emergency": gerekçe ve durdurma saati; olay raporu bağı sonra da verilir. */
export const drillStopSchema = z.strictObject({
  reason: requiredParagraph(2000),
  stoppedTime: time,
  hseReportId: nullableUuid,
});
export type DrillStopInput = z.output<typeof drillStopSchema>;

export function parseDrillStop(input: unknown): ParseResult<DrillStopInput> {
  return parsed(drillStopSchema.safeParse(input));
}

export const drillReasonSchema = z.strictObject({ reason: requiredParagraph(2000) });

export function parseDrillReason(input: unknown): ParseResult<{ reason: string }> {
  return parsed(drillReasonSchema.safeParse(input));
}

/** Durdurulan tatbikatın bağlanabileceği HSEQ raporları (ADR 0097): kaza ve ramak kala. */
export const DRILL_INCIDENT_TYPES = ['accident', 'near_miss'] as const;

/* ---------- Gönderim eksikleri ---------- */

export type DrillRecordStage =
  | 'general'
  | 'preparation'
  | 'participants'
  | 'execution'
  | 'evaluation';

export interface DrillRecordIssue {
  stage: DrillRecordStage;
  field: string;
  code: string;
}

/** En uzun tatbikat (saat): başlangıç ve bitiş saati arası; ertesi güne geçen bitiş sayılır. */
export const DRILL_MAX_HOURS = 12;

const minutesOf = (value: string) => {
  const [h, m] = value.split(':');
  return Number(h) * 60 + Number(m);
};

/** Başlangıçtan bitişe dakika; bitiş başlangıçtan önceyse ertesi gün. */
export function drillDurationMinutes(startTime: string, endTime: string): number {
  const diff = minutesOf(endTime) - minutesOf(startTime);
  return diff > 0 ? diff : diff + 24 * 60;
}

/**
 * "Submit for Master review" öncesi eksikler (belge §5, §13.3 hazırlığı). Boş liste: gönderilebilir.
 * A: saatler, yer, sorumlu, senaryo; B: brifing cevaplı, ekipman cevaplı ve hazır değilse not;
 * C: her katılımcının katılımı, gelmeyenin nedeni, en az bir katılan; D: her adımın sonucu, Not
 * Completed adımın açıklaması; E: her değerlendirme kriterinin sonucu (süre ölçümü isteğe bağlı).
 */
export function drillRecordIssues(input: {
  general: {
    startTime: string | null;
    endTime: string | null;
    location: string | null;
    responsibleUserId: string | null;
    scenario: string | null;
  };
  preparation: {
    briefingDone: boolean | null;
    equipmentReady: boolean | null;
    equipmentNotes: string | null;
  };
  participants: readonly {
    userId?: string | null;
    attendance: DrillAttendance | null;
    absenceReason: string | null;
  }[];
  steps: readonly { stepNo: number; result: DrillStepResult | null; remarks: string | null }[];
  criteria: readonly DrillCriterion[];
  evaluation: {
    results: readonly { key: string; rating: DrillRating | null; note?: string | null }[];
    crew?: readonly { userId: string; rating: DrillRating | null; note: string | null }[];
  };
}): DrillRecordIssue[] {
  const issues: DrillRecordIssue[] = [];
  const add = (stage: DrillRecordStage, field: string, code = 'REQUIRED') =>
    issues.push({ stage, field, code });
  const g = input.general;
  for (const field of [
    'startTime',
    'endTime',
    'location',
    'responsibleUserId',
    'scenario',
  ] as const) {
    if (g[field] === null) add('general', field);
  }
  if (
    g.startTime !== null &&
    g.endTime !== null &&
    drillDurationMinutes(g.startTime, g.endTime) > DRILL_MAX_HOURS * 60
  ) {
    add('general', 'endTime', 'DURATION_TOO_LONG');
  }
  const p = input.preparation;
  if (p.briefingDone === null) add('preparation', 'briefingDone');
  if (p.equipmentReady === null) add('preparation', 'equipmentReady');
  if (p.equipmentReady === false && p.equipmentNotes === null) {
    add('preparation', 'equipmentNotes');
  }
  for (const [index, person] of input.participants.entries()) {
    if (person.attendance === null) add('participants', `${index}.attendance`);
    if (person.attendance === 'absent' && person.absenceReason === null) {
      add('participants', `${index}.absenceReason`);
    }
  }
  if (!input.participants.some((person) => person.attendance === 'present')) {
    add('participants', 'present', 'NO_PARTICIPANT');
  }
  for (const step of input.steps) {
    if (step.result === null) add('execution', `${step.stepNo}.result`);
    if (step.result === 'not_completed' && step.remarks === null) {
      add('execution', `${step.stepNo}.remarks`);
    }
  }
  for (const criterion of input.criteria) {
    if (criterion.kind !== 'rating') continue;
    const result = input.evaluation.results.find((r) => r.key === criterion.key);
    if (!result?.rating) add('evaluation', criterion.key);
    // Needs Improvement gözlemi yazılır (belge §8 Part C "gözlemler, eksiklikler"; Faz 4)
    else if (result.rating === 'needs_improvement' && !result.note) {
      add('evaluation', `${criterion.key}.note`);
    }
  }
  const present = new Set(
    input.participants
      .filter((p) => p.attendance === 'present' && p.userId)
      .map((p) => String(p.userId)),
  );
  for (const person of input.evaluation.crew ?? []) {
    if (person.rating === 'needs_improvement' && !person.note) {
      add('evaluation', `crew.${person.userId}.note`);
    }
    if (person.rating !== null && !present.has(person.userId)) {
      add('evaluation', `crew.${person.userId}`, 'NOT_PRESENT');
    }
  }
  return issues;
}

/* ---------- Saat ---------- */

/**
 * Gemi yerel günü ve saati → UTC ISO (gemi saati farkıyla; CLAUDE.md "Zaman"). `after` verilirse
 * ve saat ondan önce (ya da eşit) ise ertesi gün sayılır (gece yarısını geçen bitiş).
 */
export function localTimeToUtc(
  localDay: string,
  hhmm: string,
  tzOffsetMin: number,
  after?: string | null,
): string {
  const base = Date.parse(`${localDay}T00:00:00.000Z`);
  let minutes = minutesOf(hhmm);
  if (after && minutes <= minutesOf(after)) minutes += 24 * 60;
  return new Date(base + (minutes - tzOffsetMin) * 60_000).toISOString();
}

/** UTC ISO → gemi yerel saati `HH:MM`. */
export function utcToLocalTime(at: string, tzOffsetMin: number): string {
  return new Date(Date.parse(at) + tzOffsetMin * 60_000).toISOString().slice(11, 16);
}

/* ---------- Dinlenme saati tutarlılığı ---------- */

export type DrillRestCoverage = 'covered' | 'partial' | 'missing';

/**
 * Tatbikat süresi kişinin dinlenme kaydındaki çalışma dönemleriyle örtülü mü (ADR 0018 §3:
 * tatbikat çalışmadır, istisna değildir; MLC A2.3 §7). Kayıt yazılmaz; ekran uyarır ve dinlenme
 * saatleri sayfasına bağlar (ADR 0128 Faz 3 Ek).
 */
export function drillRestCoverage(input: {
  start: string;
  end: string;
  periods: readonly { start: string; end: string }[];
}): DrillRestCoverage {
  const from = Date.parse(input.start);
  const to = Date.parse(input.end);
  if (!(to > from)) return 'covered';
  const pieces = input.periods
    .map((p) => [Math.max(Date.parse(p.start), from), Math.min(Date.parse(p.end), to)] as const)
    .filter(([a, b]) => b > a)
    .sort((a, b) => a[0] - b[0]);
  let covered = 0;
  let cursor = from;
  for (const [a, b] of pieces) {
    const start = Math.max(a, cursor);
    if (b > start) {
      covered += b - start;
      cursor = b;
    }
  }
  if (covered === 0) return 'missing';
  return covered >= to - from ? 'covered' : 'partial';
}

/* ---------- Rol önerisi ---------- */

const norm = (value: string | null | undefined) =>
  (value ?? '').normalize('NFKC').trim().toLowerCase().replace(/\s+/g, ' ');

/**
 * Katılımcının rol önerisi. Muster list ve görev kartı yapısal veri değildir (ADR 0128 Bağlam):
 * önce aynı tatbikatın son kaydında kişinin aldığı rol, yoksa şablonun ya da planın rollerinden
 * rütbe adını taşıyan rol. Bulunamazsa öneri yok; rol elle seçilir.
 */
export function suggestDrillRole(input: {
  rank: string | null;
  roles: readonly string[];
  previous: string | null;
}): { role: string; source: 'previous' | 'template' } | null {
  if (input.previous) return { role: input.previous, source: 'previous' };
  const rank = norm(input.rank);
  if (rank === '') return null;
  const match = input.roles.find((role) => {
    const r = norm(role);
    return r === rank || r.includes(rank) || rank.includes(r);
  });
  return match ? { role: match, source: 'template' } : null;
}
